import React, { useEffect, useState } from 'react';

import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import { ClientData } from './components/ClientData';
import { ClientPositions } from './components/ClientPositions';
import { getClientPersInfo } from '../../../api';
import { ClientAccountInfo } from '../../../types/clientFullInfo';
import { Nullable } from '../../../utils/types';

type ClientInfoResponse = {
  success: boolean;
  errorMessage: Nullable<string>;
  result: Nullable<ClientAccountInfo>;
};

export const ClientAccount: React.FC = () => {
  const { agreementId } = useParams<{ agreementId: string }>();
  const [response, setResponse] = useState<Nullable<ClientInfoResponse>>(null);
  const [error, setError] = useState<Nullable<string>>(null);

  useEffect(() => {
    if (!agreementId) return;

    setResponse(null);
    setError(null);

    getClientPersInfo(agreementId)
      .then((result) => setResponse(result))
      .catch((requestError) => setError(requestError.toString()));
  }, [agreementId]);

  if (!agreementId) {
    return (
      <Alert severity="info" role="status">
        Не выбран номер клиента
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <Typography variant="body2">Ошибка получения данных клиента</Typography>
        <Typography variant="body2">Ответ сервера: {error}</Typography>
      </Alert>
    );
  }

  if (!response) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" role="status">
        <CircularProgress size={20} />
        <Typography>Загружаем клиентские данные…</Typography>
      </Stack>
    );
  }

  if (!response.success) {
    return (
      <Alert severity="error">
        Ошибка получения данных клиента{response.errorMessage ? `: ${response.errorMessage}` : ''}
      </Alert>
    );
  }

  if (!response.result) {
    return <Alert severity="info">Клиентские данные отсутствуют</Alert>;
  }

  return (
    <Stack spacing={2} mt={2}>
      {response.result.client ? (
        <ClientData account={response.result} />
      ) : (
        <Alert severity="info">Данные для неподключенного клиента не отображаются</Alert>
      )}

      {response.result.clientCode && <ClientPositions agreementId={agreementId} info={response.result} />}
    </Stack>
  );
};
